/**
 * Task Descriptions
 *
 * This file contains:
 * - A mapping of RequestTypeSlugEnum to strings that are displayed in the Tasks Table's description column.
 * - A mapping of RequestTypeSlugEnum to strings that are passed in as arguments to functions that construct task descriptions
 * - Types supporting the above
 *
 * Some task descriptions rely on external data to construct (i.e. app name, employee name), the rest are strings.
 * This depends on the RequestType, and those RequestTypes will map to a function that needs arguments to construct the string.
 * The taskDescriptionsArgs constructs the arguments for these functions.
 */

import formatDate from '@/helpers/formatDate';
import { RequestTypeSlugEnum } from '@/types/requests';
import type { TFunction } from 'i18next';

type TaskDescriptions = Partial<
  Record<RequestTypeSlugEnum, string | ((param: string) => string)>
>;

// Maps Request Type to a description text to display on the Tasks table's "Tasks" column,
// some are functions that require args to construct the text
export const getTaskDescriptionsText = (
  t: TFunction<'requests', undefined>
): TaskDescriptions => ({
  [RequestTypeSlugEnum.HARDWARE_PURCHASE]: t(
    'taskDescriptions.hardwarePurchase'
  ),
  [RequestTypeSlugEnum.GET_ACCESS_OR_LICENSE]: (app: string) =>
    t('taskDescriptions.getAccessOrLicense', { app }),
  [RequestTypeSlugEnum.REMOVE_ACCESS_OR_LICENSE]: (app: string) =>
    t('taskDescriptions.removeAccessOrLicense', { app }),
  [RequestTypeSlugEnum.CREATE_ACCOUNT]: (app: string) =>
    t('taskDescriptions.createAccount', { app }),
  [RequestTypeSlugEnum.REMOVE_ACCOUNT]: (app: string) =>
    t('taskDescriptions.removeAccount', { app }),
  [RequestTypeSlugEnum.UPDATE_PROFILE]: (app: string) =>
    t('taskDescriptions.updateProfile', { app }),
  [RequestTypeSlugEnum.CHANGE_ACCESS_OR_PERMISSIONS]: t(
    'taskDescriptions.changeAccessOrPermissions'
  ),
  [RequestTypeSlugEnum.CONFIGURE_APPLICATION_SETTINGS]: t(
    'taskDescriptions.configureApplicationSettings'
  ),
  [RequestTypeSlugEnum.LOGIN_ISSUE]: t('taskDescriptions.loginIssue'),
  [RequestTypeSlugEnum.PERMISSIONS_ISSUE]: t(
    'taskDescriptions.permissionsIssue'
  ),
  [RequestTypeSlugEnum.INSTALLION_OR_SOFTWARE_UPDATE_ISSUE]: t(
    'taskDescriptions.installationOrSoftwareUpdateIssue'
  ),
  [RequestTypeSlugEnum.APPLICATIONS_OTHER]: (app: string) =>
    t('taskDescriptions.applicationsOther', { app }),
  [RequestTypeSlugEnum.INTERNET]: t('taskDescriptions.internet'),
  [RequestTypeSlugEnum.NETWORK_EQUIPMENT]: t(
    'taskDescriptions.networkEquipment'
  ),
  [RequestTypeSlugEnum.VPN]: t('taskDescriptions.vpn'),
  [RequestTypeSlugEnum.SERVER_OR_FILE_SHARES]: t(
    'taskDescriptions.serverOrFileShares'
  ),
  [RequestTypeSlugEnum.PRINTER]: t('taskDescriptions.printer'),
  [RequestTypeSlugEnum.CONFERENCE_OR_VIDEO_EQUIPMENT]: t(
    'taskDescriptions.conferenceOrVideoEquipment'
  ),
  [RequestTypeSlugEnum.CONNECTIVITY_OTHER]: t(
    'taskDescriptions.connectivityOther'
  ),
  [RequestTypeSlugEnum.SETUP_OR_PROVISIONING]: t(
    'taskDescriptions.setupOrProvisioning'
  ),
  [RequestTypeSlugEnum.SHIPPING_OR_RETURN]: t(
    'taskDescriptions.shippingOrReturn'
  ),
  [RequestTypeSlugEnum.UPDATE_SETTINGS_OR_OS]: t(
    'taskDescriptions.updateSettingsOrOs'
  ),
  [RequestTypeSlugEnum.BROKEN_OR_DAMAGED_HARDWARE]: t(
    'taskDescriptions.brokenOrDamagedHardware'
  ),
  [RequestTypeSlugEnum.SLOW_DEVICE]: t('taskDescriptions.slowDevice'),
  [RequestTypeSlugEnum.SYSTEM_OR_SETTINGS_ISSUE]: t(
    'taskDescriptions.systemOrSettingsIssue'
  ),
  [RequestTypeSlugEnum.DEVICE_LOGIN_ISSUE]: t(
    'taskDescriptions.deviceLoginIssue'
  ),
  [RequestTypeSlugEnum.FILE_OR_FOLDER_ISSUE]: t(
    'taskDescriptions.fileOrFolderIssue'
  ),
  [RequestTypeSlugEnum.DRIVE_SPACE_ISSUE]: t(
    'taskDescriptions.driveSpaceIssue'
  ),
  [RequestTypeSlugEnum.VIRUS_OR_SECURITY_ISSUE]: t(
    'taskDescriptions.virusOrSecurityIssue'
  ),
  [RequestTypeSlugEnum.DEVICE_OTHER]: t('taskDescriptions.deviceOther'),
  [RequestTypeSlugEnum.XBOARDING_CLARIFICATIONS_OR_CORRECTIONS_ONLY]: t(
    'taskDescriptions.xboardingClarificationsOrCorrectionsOnly'
  ),
  [RequestTypeSlugEnum.DOCUMENTATION]: t('taskDescriptions.documentation'),
  [RequestTypeSlugEnum.SECURITY_CONCERN_OR_QUESTION]: t(
    'taskDescriptions.securityConcernOrQuestion'
  ),
  [RequestTypeSlugEnum.SOMETHING_ELSE_OTHER]: t(
    'taskDescriptions.somethingElseOther'
  ),
  [RequestTypeSlugEnum.HRIS_ONBOARDING]: (employeeName: string) =>
    t('taskDescriptions.hrisOnboarding', { employeeName }),
  [RequestTypeSlugEnum.HRIS_OFFBOARDING]: (employeeName: string) =>
    t('taskDescriptions.hrisOffboarding', { employeeName }),
  [RequestTypeSlugEnum.INTAKE_FORM_ISSUE]: t(
    'taskDescriptions.intakeFormIssue'
  ),
  [RequestTypeSlugEnum.MDM]: (dueDate: string) =>
    t('taskDescriptions.mdm', { dueDate: formatDate(dueDate) }),
  [RequestTypeSlugEnum.ENROLLMENT_INSTRUCTIONS_REMINDER]: t(
    'taskDescriptions.enrollmentInstructionsReminder'
  ),
  [RequestTypeSlugEnum.GROUP_CHANGE_REVIEW]: (employeeName: string) =>
    t('taskDescriptions.groupChangeReview', { employeeName }),
  [RequestTypeSlugEnum.RETURN_TO_WORK]: (employeeName: string) =>
    t('taskDescriptions.returnToWork', { employeeName }),
  [RequestTypeSlugEnum.LEAVE_HANDOFF_REVERT]: (employeeName: string) =>
    t('taskDescriptions.leaveHandoffRevert', { employeeName }),
  [RequestTypeSlugEnum.LEAVE_EMAIL_FORWARDING_SETUP]: (employeeName: string) =>
    t('taskDescriptions.leaveEmailForwardingSetup', { employeeName }),
  [RequestTypeSlugEnum.INTEGRATION_NEEDS_REAUTH]: (app: string) =>
    t('taskDescriptions.integrationNeedsReauth', { app }),
});

type TaskDescriptionsFunctionArgs = {
  employeeName: string;
  appName: string;
  dueDate: string | undefined;
};

type TaskDescriptionsFunctionArgsMap = (
  args: TaskDescriptionsFunctionArgs
) => Partial<Record<RequestTypeSlugEnum, string>>;

// Returns a mapping of Request Type to arguments that are needed to construct task descriptions based on the taskDescriptionsText mapping
export const taskDescriptionsArgs: TaskDescriptionsFunctionArgsMap = ({
  employeeName,
  appName,
  dueDate,
}: TaskDescriptionsFunctionArgs) => {
  return {
    [RequestTypeSlugEnum.HRIS_ONBOARDING]: employeeName,
    [RequestTypeSlugEnum.HRIS_OFFBOARDING]: employeeName,
    [RequestTypeSlugEnum.GET_ACCESS_OR_LICENSE]: appName,
    [RequestTypeSlugEnum.REMOVE_ACCESS_OR_LICENSE]: appName,
    [RequestTypeSlugEnum.CREATE_ACCOUNT]: appName,
    [RequestTypeSlugEnum.REMOVE_ACCOUNT]: appName,
    [RequestTypeSlugEnum.APPLICATIONS_OTHER]: appName,
    [RequestTypeSlugEnum.MDM]: dueDate,
    [RequestTypeSlugEnum.GROUP_CHANGE_REVIEW]: employeeName,
    [RequestTypeSlugEnum.RETURN_TO_WORK]: employeeName,
    [RequestTypeSlugEnum.LEAVE_HANDOFF_REVERT]: employeeName,
    [RequestTypeSlugEnum.LEAVE_EMAIL_FORWARDING_SETUP]: employeeName,
    [RequestTypeSlugEnum.UPDATE_PROFILE]: appName,
    [RequestTypeSlugEnum.INTEGRATION_NEEDS_REAUTH]: appName,
  };
};

type TaskPriority = Partial<Record<RequestTypeSlugEnum, 'URGENT'>>;

export const taskPriority: TaskPriority = {
  [RequestTypeSlugEnum.MDM]: 'URGENT',
};
